import React, { useState, useEffect } from 'react';
import { Typography, Tag, Space, Tooltip, message } from 'antd';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  UserOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  UserDeleteOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  TagOutlined,
  CheckCircleFilled,
  EnvironmentOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { COLORS } from '../../../constants/colors';
import { 
  StyledTable, 
  ResponsiveScreeningButton,
  StatusBadge,
  PatientInfoContainer,
  PatientName,
  PatientEmail,
  EventTitle,
  EventLocation,
  CheckInTime,
  CheckInDate,
  TestingCodeCell,
  TestingCodeInput,
  TestingCodeSaveButton,
  StyledTableContainer,
  PaginationText,
  QueueDesktopTable,
  QueueMobileList,
  QueueMobileCard,
  QueueMobilePagination
} from '../styled';

const { Text } = Typography;

const PatientQueueTable = ({ 
  patients, 
  loading, 
  selectedEvent, 
  activeTab,
  onViewDetails,
  onActivatePreRegistration,
  onSaveTestingCode,
}) => {
  // State to force re-render for live timers
  const [, setTick] = useState(0);
  // State to track mobile viewport
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Per-row testing code drafts keyed by visit _id (undefined = use saved value)
  const [testingCodeDrafts, setTestingCodeDrafts] = useState({});
  // Tracks which row is currently saving its testing code
  const [savingTestingCodeId, setSavingTestingCodeId] = useState(null);
  // Pagination state - controlled to persist across data updates
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20
  });

  // Reset pagination to page 1 when activeTab or selectedEvent changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [activeTab, selectedEvent]);

  // Handle pagination changes
  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    });
  };

  // Default timezone (Central Time - Texas)
  const DEFAULT_TIMEZONE = 'America/Chicago';

  // Helper to get timezone from record
  const getTimezone = (record) => record?.eventType?.timezone || DEFAULT_TIMEZONE;

  // Timezone-aware date/time formatters
  const formatTime = (date, timezone) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: timezone });
  };

  const formatDate = (date, timezone) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { timeZone: timezone });
  };

  // Update timers every second - only needed on active tab where live timers run
  useEffect(() => {
    if (activeTab !== 'active') return; // No live timers on completed/archived tabs
    const interval = setInterval(() => {
      setTick(tick => tick + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTab]);

  // Track viewport width for responsive column widths
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resolve the current value for a row's testing code input
  const getTestingCodeValue = (record) => {
    const draft = testingCodeDrafts[record._id];
    return draft !== undefined ? draft : (record.patient?.testingCode || '');
  };

  // Save a single row's testing code via the parent handler
  const handleSaveTestingCode = async (record) => {
    if (!onSaveTestingCode) return;
    const patientId = record.patient?.patientId || record.patientId;
    if (!patientId) {
      message.error('Cannot save — missing patient ID');
      return;
    }
    const value = getTestingCodeValue(record).trim();
    setSavingTestingCodeId(record._id);
    try {
      await onSaveTestingCode(record, value);
      // Keep the saved value as the draft so the field stays in sync until refresh
      setTestingCodeDrafts(prev => ({ ...prev, [record._id]: value }));
    } catch {
      // Parent already surfaces the error message
    } finally {
      setSavingTestingCodeId(null);
    }
  };

  // Inline test-kit code editor, shared by the table column and mobile cards.
  const renderTestingCodeEditor = (record) => {
    const patientId = record.patient?.patientId || record.patientId;
    const original = record.patient?.testingCode || '';
    const value = getTestingCodeValue(record);
    const isSaving = savingTestingCodeId === record._id;
    const isDirty = value.trim() !== original;
    const hasSaved = !!original && !isDirty;
    return (
      <TestingCodeCell onClick={(e) => e.stopPropagation()}>
        <TestingCodeInput
          size="small"
          placeholder="Add Test Kit Code"
          prefix={<TagOutlined style={{ fontSize: 12 }} />}
          suffix={hasSaved ? (
            <Tooltip title="Saved">
              <CheckCircleFilled style={{ color: COLORS.healthyGreen, fontSize: 13 }} />
            </Tooltip>
          ) : <span />}
          value={value}
          disabled={isSaving || !patientId}
          onChange={(e) =>
            setTestingCodeDrafts(prev => ({ ...prev, [record._id]: e.target.value }))
          }
          onClick={(e) => e.stopPropagation()}
          onPressEnter={() => isDirty && handleSaveTestingCode(record)}
        />
        <Tooltip title={isDirty ? 'Save testing code' : hasSaved ? 'Saved' : 'Enter a code to save'}>
          <TestingCodeSaveButton
            size="small"
            type="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            disabled={isSaving || !isDirty || !patientId}
            onClick={(e) => {
              e.stopPropagation();
              handleSaveTestingCode(record);
            }}
          />
        </Tooltip>
      </TestingCodeCell>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      pre_registered: '#722ed1', // Purple for pre-registered
      pending_review: COLORS.warningAmber,
      matched: COLORS.healthyGreen,
      new: COLORS.primary, // Updated from 'new_patient' to 'new'
      new_patient: COLORS.primary, // Keep for backwards compatibility
      duplicate: COLORS.criticalRed,
      needs_attention: COLORS.error,
      in_progress: COLORS.$primaryBlue,
      completed: COLORS.healthyGreen,
      archived: COLORS.textMuted,
      'no-show': COLORS.criticalRed
    };
    return colors[status] || COLORS.textMuted;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pre_registered: <ClockCircleOutlined />,
      pending_review: <ClockCircleOutlined />,
      matched: <CheckOutlined />,
      new: <UserOutlined />, // Updated from 'new_patient' to 'new'
      new_patient: <UserOutlined />, // Keep for backwards compatibility
      duplicate: <WarningOutlined />,
      needs_attention: <ExclamationCircleOutlined />,
      in_progress: <ClockCircleOutlined />,
      completed: <CheckCircleOutlined />,
      archived: <InboxOutlined />,
      'no-show': <UserDeleteOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const columns = [
    {
      title: activeTab === 'active' ? '#' : 'Completed',
      dataIndex: activeTab === 'active' ? 'queuePosition' : 'completionTime',
      key: activeTab === 'active' ? 'position' : 'completed',
      width: activeTab === 'active' ? 56 : 110,
      align: 'center',
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      hidden: activeTab === 'completed' || activeTab === 'archived' || activeTab === 'needs_attention', // Hide for Completed, Archived, and Needs Attention tabs
      sorter: (a, b) => {
        if (activeTab === 'active') {
          return (a.calculatedPosition || 0) - (b.calculatedPosition || 0);
        }
        return new Date(a.completionTime || 0) - new Date(b.completionTime || 0);
      },
      render: (value, record) => {
        if (activeTab === 'active') {
          const activeStatuses = ['new', 'new_patient', 'in_progress', 'pending_review']; // Added 'new' status
          
          if (activeStatuses.includes(record.status)) {
            const displayPosition = record.calculatedPosition;
            
            return (
              <StatusBadge 
                color={record.priority === 'urgent' ? COLORS.criticalRed :
                      record.priority === 'high' ? COLORS.warningAmber :
                      COLORS.$primaryBlue}
                title={`Queue position ${displayPosition} - Priority: ${record.priority || 'normal'}`}
              >
                {displayPosition}
              </StatusBadge>
            );
          }
        } else {
          // Show completion time for completed/archived patients
          return (
            <Text style={{ fontSize: '12px', color: COLORS.textMedium }}>
              {value ? new Date(value).toLocaleDateString() : '-'}
            </Text>
          );
        }
        return <span style={{ color: COLORS.textMuted }}>-</span>;
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 128,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      hidden: activeTab === 'archived' || activeTab === 'no_show', // Hide status column for archived and no_show tabs since we know all have the same status
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (record) => {
        const getStatusDisplayShort = (status, hasSaved) => {
          const statusMap = {
            'pending_review': 'Pending Review',
            'matched': 'Matched',
            'new': 'New Patient', // Updated from 'new_patient' to 'new'
            'new_patient': 'New Patient', // Keep for backwards compatibility
            'duplicate': 'Duplicate',
            'needs_attention': 'Needs Attention',
            'in_progress': hasSaved ? 'In Progress - Saved' : 'In Progress',
            'completed': 'Completed',
            'archived': 'Archived',
            'no-show': 'No Show'
          };
          return statusMap[status] || status;
        };

        // Check if this visit has partial saves (lastSavedAt exists but not completed)
        const hasSaved = record.lastSavedAt && !record.completionTime;
        const timezone = getTimezone(record);

        return (
          <Space size={4} align="center">
            <Tag
              color={getStatusColor(record.status)}
              icon={getStatusIcon(record.status)}
              style={{ 
                fontWeight: 600, 
                fontSize: '11px',
                padding: '4px 8px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                border: 'none',
                lineHeight: 1.2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                margin: 0
              }}
            >
              {getStatusDisplayShort(record.status, hasSaved)}
            </Tag>
            {hasSaved && (
              <Tooltip title={`Last saved by ${record.lastSavedByName || 'Staff'} at ${formatTime(record.lastSavedAt, timezone)}`}>
                <SaveOutlined style={{ fontSize: '14px', color: COLORS.$primaryBlue, cursor: 'help' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Member',
      key: 'patient',
      sorter: (a, b) => {
        const aName = `${a.patient?.firstName || ''} ${a.patient?.lastName || ''}`.trim().toLowerCase();
        const bName = `${b.patient?.firstName || ''} ${b.patient?.lastName || ''}`.trim().toLowerCase();
        return aName.localeCompare(bName);
      },
      render: (record) => (
        <PatientInfoContainer>
          <PatientName>
            {record.patient?.firstName || 'Unknown'} {record.patient?.lastName || 'Member'}
            {record.patient?.usedBefore && (
              <Tag
                color="red"
                style={{
                  fontSize: '10px',
                  lineHeight: '16px',
                  padding: '0 5px',
                  marginLeft: '6px',
                  borderRadius: '3px',
                  fontWeight: 500,
                  verticalAlign: 'middle',
                  border: 'none'
                }}
              >
                Returning
              </Tag>
            )}
          </PatientName>
          <PatientEmail>
            {record.patient?.emailAddress || 'Not provided'}
          </PatientEmail>
        </PatientInfoContainer>
      ),
      width: 200,
      ellipsis: true
    },
    // Notes column for Needs Attention tab - placed immediately after Member column
    {
      title: 'Notes',
      key: 'notes',
      hidden: activeTab !== 'needs_attention',
      render: (record) => (
        <span
          title={record.notes || ''}
          style={{
            display: 'inline-block',
            maxWidth: 300,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: record.notes ? COLORS.textDark : COLORS.textMuted
          }}
        >
          {record.notes || '-'}
        </span>
      ),
      width: 320
    },
    {
      title: 'Event',
      key: 'event',
      hidden: selectedEvent !== 'all', // Hide this column when not viewing all events
      sorter: (a, b) => (a.eventInfo?.eventTitle || '').localeCompare(b.eventInfo?.eventTitle || ''),
      render: (record) => {
        if (record.eventInfo) {
          return (
            <Space direction="vertical" size={2}>
              <EventTitle>
                {record.eventInfo.eventTitle || 'Untitled Event'}
              </EventTitle>
              <EventLocation>
                {record.eventInfo.eventLocation || 'Location TBD'}
              </EventLocation>
            </Space>
          );
        }
        return <span style={{ color: COLORS.textMuted }}>-</span>;
      },
      width: 160,
      ellipsis: true
    },
    {
      title: 'Check-in',
      dataIndex: 'checkInTime',
      key: 'checkInTime',
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => new Date(a.checkInTime || 0) - new Date(b.checkInTime || 0),
      render: (time, record) => {
        const timezone = getTimezone(record);
        return (
          <Space direction="vertical" size={2}>
            <CheckInTime>
              {formatTime(time, timezone)}
            </CheckInTime>
            <CheckInDate>
              {formatDate(time, timezone)}
            </CheckInDate>
          </Space>
        );
      },
      width: 78
    },
    {
      title: 'Testing Code',
      key: 'testingCode',
      width: 230,
      hidden: activeTab === 'archived' || activeTab === 'no_show', // Quick-entry available on active/queue tabs
      render: (record) => renderTestingCodeEditor(record)
    },
    {
      title: 'Wait Time',
      key: 'waitingTime',
      hidden: true, // Hidden per stakeholder request - save indicator moved to Status column
      render: () => null,
      width: 0
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? 50 : 240,
      fixed: 'right',
      hidden: activeTab === 'active' || activeTab === 'completed' || activeTab === 'archived' || activeTab === 'no_show' || activeTab === 'needs_attention', // Only show Actions column for the pre-registered tab (Activate). Screening is started by clicking a row to open the patient details modal.
      render: (record) => {
        return (
          <Space size={4} wrap>
            {/* Show Activate button for pre-registered tab */}
            {activeTab === 'pre_registered' && (
              <Tooltip title="Activate and start timer - moves patient to active queue">
                <ResponsiveScreeningButton
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onActivatePreRegistration) {
                      onActivatePreRegistration(record);
                    }
                  }}
                  style={{ 
                    fontWeight: 'normal',
                    background: '#722ed1',
                    borderColor: '#722ed1'
                  }}
                >
                  Activate
                </ResponsiveScreeningButton>
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  // Filter out hidden columns
  const visibleColumns = columns.filter(column => !column.hidden);

  // Mobile card for a single queue record (mirrors the table columns).
  const renderQueueCard = (record) => {
    const activeStatuses = ['new', 'new_patient', 'in_progress', 'pending_review'];
    const showNum = activeTab === 'active' && activeStatuses.includes(record.status) && record.calculatedPosition != null;
    const showCode = activeTab !== 'archived' && activeTab !== 'no_show';
    const timezone = getTimezone(record);
    const location = record.eventInfo
      ? `${record.eventInfo.eventTitle || 'Untitled Event'}${record.eventInfo.eventLocation ? ' — ' + record.eventInfo.eventLocation : ''}`
      : null;

    return (
      <QueueMobileCard
        key={record._id}
        onClick={(e) => {
          if (e.target.closest('button') || e.target.closest('input') || e.target.closest('.ant-input-affix-wrapper')) return;
          onViewDetails(record);
        }}
      >
        <div className="qc-head">
          {showNum && <span className="qc-num">{record.calculatedPosition}</span>}
          <div className="qc-main">
            <div className="qc-name">
              <span>{record.patient?.firstName || 'Unknown'} {record.patient?.lastName || 'Member'}</span>
              {record.patient?.usedBefore && (
                <Tag
                  color="red"
                  style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px', borderRadius: '3px', fontWeight: 500, border: 'none', margin: 0 }}
                >
                  Returning
                </Tag>
              )}
            </div>
            <div className="qc-email">{record.patient?.emailAddress || 'Not provided'}</div>
            {location && (
              <div className="qc-location">
                <EnvironmentOutlined />
                <span>{location}</span>
              </div>
            )}
          </div>
          <div className="qc-time">
            <div className="qc-time-value">{formatTime(record.checkInTime, timezone)}</div>
            <div className="qc-time-date">{formatDate(record.checkInTime, timezone)}</div>
          </div>
        </div>

        {showCode && <div className="qc-code">{renderTestingCodeEditor(record)}</div>}

        {activeTab === 'pre_registered' && (
          <div className="qc-actions">
            <ResponsiveScreeningButton
              type="primary"
              icon={<PlayCircleOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onActivatePreRegistration) onActivatePreRegistration(record);
              }}
              style={{ fontWeight: 'normal', background: '#722ed1', borderColor: '#722ed1' }}
            >
              Activate
            </ResponsiveScreeningButton>
          </div>
        )}
      </QueueMobileCard>
    );
  };

  const mobileTotalPages = Math.max(1, Math.ceil(patients.length / pagination.pageSize));
  const mobileStart = (pagination.current - 1) * pagination.pageSize;
  const mobilePageItems = patients.slice(mobileStart, mobileStart + pagination.pageSize);

  return (
    <div className="table-animate-in" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <StyledTableContainer style={{ position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px'
          }}>
            <HeartLoader size="40px" padding="0" />
          </div>
        )}
        <QueueDesktopTable>
          <StyledTable
            columns={visibleColumns}
            dataSource={patients}
            rowKey="_id"
            loading={false}
            size="middle"
            components={{
              header: {
                cell: (props) => (
                  <th 
                    {...props} 
                    style={{ 
                      ...props.style, 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }} 
                  />
                )
              }
            }}
            onRow={(record) => ({
              onClick: (e) => {
                // Don't trigger row click if clicking on buttons, dropdowns, or their children
                if (e.target.closest('button') || 
                    e.target.closest('.ant-dropdown') || 
                    e.target.closest('.ant-dropdown-trigger') ||
                    e.target.closest('.ant-btn')) {
                  return;
                }
                onViewDetails(record);
              },
              style: { cursor: 'pointer' }
            })}
            onChange={handleTableChange}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => (
                <PaginationText>
                  Showing {range[0]}-{range[1]} of {total} members
                </PaginationText>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
              className: 'patient-queue-pagination'
            }}
            scroll={{ x: 800, y: 1 }}
            rowClassName={(record) => {
              if (record.status === 'in_progress') return 'in-progress-row';
              if (record.priority === 'urgent') return 'urgent-row';
              return '';
            }}
          />
        </QueueDesktopTable>

        <QueueMobileList>
          {mobilePageItems.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '32px 0' }}>
              No members found
            </div>
          ) : (
            mobilePageItems.map(renderQueueCard)
          )}
        </QueueMobileList>

        {patients.length > 0 && (
          <QueueMobilePagination>
            <button
              type="button"
              className="pg-btn"
              disabled={pagination.current <= 1}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            >
              <LeftOutlined /> Prev
            </button>
            <span className="pg-info">
              Page <strong>{pagination.current}</strong> of {mobileTotalPages}
            </span>
            <button
              type="button"
              className="pg-btn"
              disabled={pagination.current >= mobileTotalPages}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            >
              Next <RightOutlined />
            </button>
          </QueueMobilePagination>
        )}
      </StyledTableContainer>
    </div>
  );
};

export default PatientQueueTable;
